/**
 * Theme-palette persistence helpers, shared by every web app.
 *
 * The BraveMove palette key is `<brand>_<mode>` — one of four: slate/garnet ×
 * light/dark. The user may pick ANY of the four; the choice is persisted as
 * the full palette key and re-applied at boot. `<BraveMoveThemeProvider>` itself
 * does NOT persist — these helpers add localStorage on top of it.
 */

export type ThemeMode = 'light' | 'dark';
export type ThemeBrand = 'slate' | 'garnet';
/** Matches `@bravemove/design-tokens`' `PaletteKey` (same literal union). */
export type PaletteKey = `${ThemeBrand}_${ThemeMode}`;

/** All selectable palettes, in display order. */
export const PALETTE_KEYS: readonly PaletteKey[] = [
  'garnet_light',
  'garnet_dark',
  'slate_light',
  'slate_dark',
];

/** English fallback names; screens translate them with `common:theme.names.*`. */
export const PALETTE_LABELS: Readonly<Record<PaletteKey, string>> = {
  slate_light: 'Slate · Light',
  slate_dark: 'Slate · Dark',
  garnet_light: 'Garnet · Light',
  garnet_dark: 'Garnet · Dark',
};

export const THEME_PALETTE_STORAGE_KEY = 'bravemove-theme-palette';
export const THEME_APPEARANCE_STORAGE_KEY = 'bravemove-marketing-appearance';

function isPaletteKey(v: string | null): v is PaletteKey {
  return v !== null && (PALETTE_KEYS as readonly string[]).includes(v);
}

/** Read the previously-chosen palette, or `null` if none/unavailable. */
export function readStoredPalette(): PaletteKey | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(THEME_PALETTE_STORAGE_KEY);
    return isPaletteKey(v) ? v : null;
  } catch {
    return null;
  }
}

/** Persist the chosen palette. No-op when storage is unavailable (private mode). */
export function writeStoredPalette(palette: PaletteKey): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_PALETTE_STORAGE_KEY, palette);
    // An explicit choice must also replace marketing's optional system mode.
    window.localStorage.setItem(
      THEME_APPEARANCE_STORAGE_KEY,
      palette.endsWith('_dark') ? 'dark' : 'light',
    );
  } catch {
    /* storage disabled — non-fatal */
  }
}

/**
 * The palette an app should boot with: the stored choice, else the app's
 * default. Use in `main.tsx`:
 *
 *   <BraveMoveThemeProvider initial={resolveInitialPalette('slate_light')}>
 */
export function resolveInitialPalette(defaultPalette: PaletteKey): PaletteKey {
  return readStoredPalette() ?? defaultPalette;
}
